import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import DoubleArrowIcon from '@material-ui/icons/DoubleArrow';
import DragHandleIcon from '@material-ui/icons/DragHandle';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import moment from 'moment';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableProps } from 'src/types/components/tableList.types';
import Scrollbar from 'src/components/Scrollbar';
// import logger from 'src/utils/logger';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ColumnConfig from 'src/components/ColumnConfig';
import { DragDropContext } from 'react-beautiful-dnd';

const useStyles = makeStyles<Theme>(() =>
  createStyles({
    btn: {
      width: '47px',
      height: '40px',
      padding: 0,
      minWidth: 'initial',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      color: '#fff',
      marginLeft: '13px',
      '& .MuiButton-iconSizeMedium': {
        margin: '0',
      },
    },
    clip: {
      display: 'inline-block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '10ch',
    },
    textFieldSelect: {
      width: '230px',
      marginLeft: '13px',
    },
    textFieldSelectRoot: {
      borderRadius: '4px',
      height: '38px',
    },
    tabButton: {
      width: '181px',
    },
    cardRoot: {
      borderRadius: '4px',
    },
    tabs: {
      '& .MuiTab-root': {
        backgroundColor: 'transparent',
        minWidth: '120px',
      },
      '& .Mui-selected': {
        backgroundColor: '#231F20',
        color: '#fff',
      },
    },
    tableHead: {
      '& .MuiTableCell-root': {
        textTransform: 'capitalize',
      },
    },
    tableRow: {
      '& :hover': {
        cursor: 'pointer',
      },
      '& .ticketStatusTrue': {
        backgroundColor: 'rgba(7, 83, 0, 0.16)',
        color: '#1D7A15',
        padding: '5px',
        fontWeight: 'Bold',
      },
      '& .ticketStatusFalse': {
        background: '#DEEBFF',
        color: '#3375D7',
        padding: '5px',
        fontWeight: 'Bold',
      },
    },
    priorityComponent: {
      height: '30px',
      background: 'rgba(189,189,189,0.1)',
      color: '#000000aa',
      width: '100px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      textTransform: 'capitalize',
      justifyContent: 'center',
    },
    loaderTable: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: '2222',
    },
  })
);

const TableHeadComponent: FC<any> = ({
  data,
  setSelectedSort,
  selectedSort,
}: any) => (
  <TableHead sx={{ marginBottom: '1rem' }}>
    <TableRow>
      {data?.map((col: any) => (
        <TableCell
          key={col.id}
          align="center"
          className="textBold"
          sortDirection={
            selectedSort?.key === col?.id ? selectedSort?.value : false
          }
          title={col?.toolTip ? col?.summary : null}>
          {(() => {
            if (col.id === 'attachment') {
              return '';
            }

            if (col.isSort) {
              return (
                <TableSortLabel
                  active={selectedSort?.key === col?.id}
                  direction={
                    selectedSort?.key === col?.id ? selectedSort?.value : 'asc'
                  }
                  onClick={() => {
                    const newDirection =
                      selectedSort?.key === col?.id &&
                      selectedSort?.value === 'asc'
                        ? 'desc'
                        : 'asc';

                    col.toggleSortData(`${col.id}`);
                    setSelectedSort({
                      key: `${col?.id}`,
                      value: newDirection,
                    });
                  }}>
                  {col?.label}
                </TableSortLabel>
              );
            }

            return col?.label;
          })()}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const PriorityComponent: FC<any> = ({ label }: any) => {
  const classes = useStyles();
  const styling: any = {
    icon: <RadioButtonUncheckedIcon />,
  };

  switch (label?.toLowerCase()) {
    case 'critical':
      styling.icon = (
        <WarningAmberRoundedIcon
          sx={{
            fontSize: '16px',
            color: '#f55033',
          }}
        />
      );
      break;
    case 'highest':
      styling.icon = (
        <DoubleArrowIcon
          sx={{
            fontSize: '16px',
            color: '#f55033',
            transform: 'rotate(270deg)',
          }}
        />
      );
      break;
    case 'medium':
      styling.icon = (
        <DragHandleIcon
          sx={{ fontSize: '24px', width: '18px', color: '#f79d28' }}
        />
      );
      break;
    case 'minor':
      styling.icon = <RadioButtonUncheckedIcon sx={{ fontSize: '14px' }} />;
      break;
    case 'blocker':
      styling.icon = (
        <RemoveCircleIcon sx={{ fontSize: '16px', color: '#f55033' }} />
      );
      break;
    case 'high':
      styling.icon = (
        <ExpandLessIcon sx={{ fontSize: '24px', color: '#f55033' }} />
      );
      break;
    case 'low':
      styling.icon = (
        <ExpandMoreIcon sx={{ fontSize: '24px', color: '#3384f5' }} />
      );
      break;
    case 'lowest':
      styling.icon = (
        <DoubleArrowIcon
          sx={{
            fontSize: '16px',
            color: '#3384f5',
            transform: 'rotate(90deg)',
          }}
        />
      );
      break;
    default:
      styling.icon = label?.toLowerCase()?.indexOf('back') === -1 ? 'N/A' : '';
      break;
  }
  return (
    <Box className={classes.priorityComponent}>
      {styling.icon}
      {label?.toLowerCase()}
    </Box>
  );
};

const TimeComponent: FC<any> = ({ timeEstimate, totalLoggedTime }: any) => {
  const getTimeColor = () => {
    if (!timeEstimate || !totalLoggedTime) return '#3384f5';
    return totalLoggedTime > timeEstimate ? '#ff0000' : '#3384f5';
  };

  const convertSecondsToTimeString = (seconds) => {
    if (!seconds) return '0h';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    let result = '';
    if (hours > 0) {
      result += `${hours}h `;
    }
    if (minutes > 0) {
      result += `${minutes}m`;
    }

    return result.trim();
  };

  return (
    <Box sx={{ color: getTimeColor() }}>
      {convertSecondsToTimeString(totalLoggedTime)}/
      {convertSecondsToTimeString(timeEstimate)}
    </Box>
  );
};

const TableBodyComponent: FC<any> = ({
  data,
  editRouteUrl,
  permissions,
  isProject,
  isOrganization,
  newTicketIds,
  visibleColumns,
}: any) => {
  const classes = useStyles();

  const renderCellContent = (column, row) => {
    const getValue = (path: string, obj: any) =>
      path.split('.').reduce((acc, key) => acc?.[key], obj);

    const boldFields = [
      'assignee',
      'createdBy',
      'escalationManager',
      'stakeHolder',
      'projectSponsor',
      'updatedBy',
      'approvedBy',
      'collaborators',
    ];

    const wrapInBold = (content: React.ReactNode, columnId: string) => {
      if (boldFields.includes(columnId)) {
        return <span style={{ fontWeight: 600 }}>{content}</span>;
      }
      return <span>{content}</span>;
    };

    const renderDocLinks = (links: Array<{ label: string; url: string }>) => {
      if (!links || links.length === 0) return '-';

      return (
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}>
          {links.map((link, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>•</span>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#3384f5' }}
                onClick={(e) => {
                  e.stopPropagation();
                }}>
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      );
    };

    switch (column.id) {
      case 'attachment':
        return row?.attachmentId?.length ? (
          <AttachFileIcon sx={{ fontSize: '18px' }} />
        ) : null;

      case 'id':
        return <span className={classes.clip}>{row?.caseKey}</span>;

      case 'priority':
        return <PriorityComponent label={row?.priority?.name} />;

      case 'totalLoggedTime':
        return (
          <TimeComponent
            timeEstimate={row?.timeEstimate}
            totalLoggedTime={row?.totalLoggedTime}
          />
        );

      case 'createdAt':
      case 'updatedAt':
      case 'dueDate':
      case 'approvedDate':
        return (
          <span>
            {row[column.id] ? moment(row[column.id]).format('DD-MM-YYYY HH:mm:ss') : '-'}
          </span>
        );

      case 'docUrls.figmaLinks':
        return renderDocLinks(row?.docUrls?.figmaLinks);

      case 'docUrls.googleDocLinks':
        return renderDocLinks(row?.docUrls?.googleDocLinks);

      case 'isApproved':
        return (
          <span
            style={{
              color: !row?.caseKey?.startsWith('AFR') || row?.isApproved ? '#1D7A15' : '#666',
              fontWeight: 500,
            }}>
            {!row?.caseKey?.startsWith('AFR') || row?.isApproved ? 'Yes' : 'No'}
          </span>
        );

      case 'reOpened':
        return (
          <span
            style={{
              color: row?.reOpened ? '#f55033' : '#666',
              fontWeight: 500,
            }}>
            {row?.reOpened ? 'Yes' : 'No'}
          </span>
        );

      default: {
        const value = getValue(column.id, row);

        if (value == null) {
          return wrapInBold('-', column.id);
        }

        if (typeof value === 'object' && Object.keys(value).length === 0) {
          return wrapInBold('-', column.id);
        }

        if (Array.isArray(value)) {
          return wrapInBold(
            value.map((item) => item?.name || '-').join(', ') || '-',
            column.id
          );
        }

        if (value?.name) {
          return wrapInBold(value.name, column.id);
        }

        if (typeof value === 'object') {
          return wrapInBold('-', column.id);
        }

        return wrapInBold(value || '-', column.id);
      }
    }
  };

  return (
    <TableBody style={{ position: 'relative' }}>
      {data?.length !== 0 ? (
        data?.map((row: any) => (
          <TableRow
            component={Link}
            to={
              permissions?.update || permissions?.getById
                ? `${editRouteUrl}/${row?.caseKey}`
                : ''
            }
            hover
            className={classes.tableRow}
            key={row?.id}
            style={{
              border: newTicketIds.includes(row.id)
                ? '2px solid #87c387'
                : 'transparent',
              textDecoration: 'none',
            }}>
            {visibleColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.id === 'title' ? 'left' : 'center'}
                size={column.id === 'attachment' ? 'small' : 'medium'}
                sx={column.id === 'attachment' ? { padding: '8px 0' } : {}}>
                {renderCellContent(column, row)}
              </TableCell>
            ))}
          </TableRow>
        ))
      ) : (
        <TableRow selected>
          <TableCell
            colSpan={visibleColumns.length}
            sx={{ textAlign: 'center' }}>
            No Data Found
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
};

interface CasesTableTypes extends TableProps {
  sortData?: any;
  toggleSortData?: any;
  setSelectedSort?: any;
  selectedSort?: any;
  isOrganization?: boolean;
  isProject?: boolean;
  columns?: any;
  setColumns?: any;
}
const CasesTable: FC<CasesTableTypes> = ({
  data,
  editRouteUrl,
  count,
  page,
  limit,
  handlePage,
  bulkIds,
  handleLimit,
  handleAddBulkIds,
  handleRemoveBulkIds,
  sortData,
  toggleSortData,
  selectedSort,
  setSelectedSort,
  permissions,
  isOrganization = true,
  isProject = true,
  newTicketIds,
  columns,
  setColumns,
}: any) => {
  const classes = useStyles();

  // Filter columns based on organization and project settings
  const filteredColumns = columns.filter((col) => {
    if (!isOrganization && col.id === 'organization') return false;
    if (!isProject && col.id === 'project') return false;
    return true;
  });

  // Get visible columns for the table
  const visibleColumns = filteredColumns.filter(
    (col) => col.isVisible !== false
  );

  const handleChangePage = useCallback(
    (event: any, _page: number) => {
      handlePage(_page);
    },
    [page, data]
  );

  const handleChangeLimit = useCallback(
    (event: any) => {
      handleLimit(parseInt(event.target.value, 10));
      handlePage(0);
    },
    [limit, data]
  );

  return (
    <Box sx={{ backgroundColor: 'background.default', py: 1 }}>
      <Card className={classes.cardRoot}>
        <Scrollbar>
          <Box sx={{ minWidth: 700, px: 1 }}>
            <Table>
              <TableHeadComponent
                data={visibleColumns}
                selectedSort={selectedSort}
                setSelectedSort={setSelectedSort}
              />
              <TableBodyComponent
                data={data}
                editRouteUrl={editRouteUrl}
                handleAddBulkIds={handleAddBulkIds}
                bulkIds={bulkIds}
                handleRemoveBulkIds={handleRemoveBulkIds}
                permissions={permissions}
                isProject={isProject}
                isOrganization={isOrganization}
                newTicketIds={newTicketIds}
                visibleColumns={visibleColumns}
              />
            </Table>
          </Box>
        </Scrollbar>
        <TablePagination
          component="div"
          count={count}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeLimit}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100, 500, 1000]}
        />
      </Card>
    </Box>
  );
};

export default CasesTable;
